import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchPowerHeatmap } from "@/lib/api";
import { useDashboardOrg } from "@/app/DashboardOrgContext";
import {ApexHeatmapChart, CHART_COLORS} from "@/components/ApexChart";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Card, CardContent } from "@/components/ui/card";

type Days = 3 | 30 | 90 | 365;

export function PowerHeatmap() {
  const [days, setDays] = useState<Days>(3);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Admin → fleet heatmap; manager → the selected org's heatmap
  // (/api/org/<id>/power-heatmap). scopeReady gates the manager
  // pre-selection window and keeps driver-only users from firing anything.
  const { dashboardOrgId, scopeReady } = useDashboardOrg();

  // The heatmap's hourly cells fill in as the aggregator samples (every
  // 15 min); polling every minute was pure waste.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["power-heatmap", dashboardOrgId, days, tz],
    queryFn: () => fetchPowerHeatmap(days, tz, dashboardOrgId),
    enabled: scopeReady,
    refetchInterval: 15 * 60_000,
    staleTime: 10 * 60_000,
  });
  
  const getRangeLabel =(d: Days) => {
    switch (d) {
      case 3:
        return "3 days";
      case 30:
        return "Last month";
      case 90:
        return "3 months";
      case 365:
        return "12 months";
    }
  }

  // Not ready = manager without a selected org yet, or a driver-only user
  // (never becomes ready) — render nothing rather than poll into a 403.
  if (!scopeReady) return null;

  const rawSeries = data?.series ?? [];
  // 90d keeps daily rows but compresses them (~7px vs 18px) so the quarter
  // fits one screen; at that density only every 7th y label is shown to
  // avoid overlap (blank names render as unlabeled rows).
  const compact = days === 90;
  const series = compact
    ? rawSeries.map((s, i) => (i % 7 === 0 ? s : { ...s, name: "" }))
    : rawSeries;
  const rowHeight = compact ? 7 : 18;
  const chartHeight = Math.min(compact ? 700 : 600, Math.max(200, series.length * rowHeight + 60));

  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <h6 className="text-base font-semibold">Power Usage</h6>
          <ToggleGroup type="single" size="sm" variant="outline" value={String(days)} onValueChange={(v) => v && setDays(Number(v) as Days)}>
            {([3, 30, 90, 365] as Days[]).map((d) => (
              <ToggleGroupItem key={d} value={String(d)}>
                {getRangeLabel(d)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : isError ? (
          <div className="rounded-lg bg-destructive/10 text-destructive p-3 text-sm">Failed to load heatmap data</div>
        ) : series.length === 0 ? (
          <div className="py-10 text-center opacity-60 text-sm">No power data available yet</div>
        ) : (
          <ApexHeatmapChart
            series={series}
            height={chartHeight}
            // showDataLabels
            maxValue={data?.max_value ?? 1}
          />
        )}
      </CardContent>
    </Card>
  );
}
